import React, { useEffect, useRef, useState } from "react";
import { Stage, Layer, Image as KonvaImage, Line } from "react-konva";
import useImage from "use-image";
import { randomFruit } from "./utils";
import { useSpring, animated } from "@react-spring/konva";
import { Howl } from "howler";
import { userGameRewards, getUserDetails1 } from "../../../../apis/user";
import { usePageVisibility } from "./hooks/usePageVisibility";
import useUserInfo from "../../../../Hooks/useUserInfo";
const FRUIT_SIZE = 100;
const HALF_WIDTH = FRUIT_SIZE / 2;
const HALF_HEIGHT = FRUIT_SIZE * 0.6;

const sliceSound = new Howl({
  src: ["/sounds/slice.mp3"],
  volume: 0.6,
  preload: true,
});
const bombSound = new Howl({
  src: ["/sounds/bomb.mp3"],
  volume: 0.8,
  preload: true,
});

const FruitImage = ({ x, y, image, sliced, id, removeSliced }) => {
  const [img] = useImage(`/images/${image}`);
  const [leftImg] = useImage(`/images/${image.replace(".png", "-left.png")}`);
  const [rightImg] = useImage(`/images/${image.replace(".png", "-right.png")}`);
  const [showHalves, setShowHalves] = useState(false);
  // Scale in animation on spawn
  const [springs, api] = useSpring(() => ({
    from: { scale: 0 },
    to: { scale: 1 },
    config: { tension: 150, friction: 12 },
  }));

  useEffect(() => {
    api.start();
  }, [api]);

  useEffect(() => {
    if (sliced) {
      setShowHalves(true);
      const timer = setTimeout(() => removeSliced(id), 900);
      return () => clearTimeout(timer);
    }
  }, [sliced]);

  if (showHalves) {
    return (
      <>
        <AnimatedHalf image={leftImg} startX={x} startY={y} direction={-1} />
        <AnimatedHalf image={rightImg} startX={x} startY={y} direction={1} />
      </>
    );
  }

  return (
    <animated.Image
      image={img}
      x={x - HALF_WIDTH}
      y={y - HALF_WIDTH}
      width={FRUIT_SIZE}
      height={FRUIT_SIZE}
      shadowBlur={15}
      shadowColor="black"
      scale={springs.scale}
    />
  );
};

const AnimatedHalf = ({ image, startX, startY, direction }) => {
  const [props] = useSpring(() => ({
    from: { x: startX, y: startY, rotation: 0, opacity: 1 },
    to: async (next) => {
      await next({
        x: startX + direction * 150,
        y: startY + 150,
        rotation: direction * 90,
        opacity: 0,
      });
    },
    config: { duration: 900 },
  }));

  return (
    <animated.Image
      image={image}
      x={props.x}
      y={props.y}
      rotation={props.rotation}
      opacity={props.opacity}
      width={HALF_WIDTH}
      height={HALF_HEIGHT}
      offsetX={HALF_WIDTH / 2}
      offsetY={HALF_HEIGHT / 2}
    />
  );
};

const handleApiResponse = async (
  points,
  updateUserInfo,
  updatewatchScreenInfo,
  setApiLoading
) => {
  if (points <= 0) {
    return;
  }

  setApiLoading(true);
  updateUserInfo((prev) => ({
    ...prev,
    isLoading: true,
  }));

  try {
    const apiData = {
      gamePoints: points,
      gameType: "fruitslice",
    };
    const res = await userGameRewards(apiData);

    if (res) {
      console.log("Score submitted successfully:", res);
    }
  } catch (error) {
    console.error("API call failed:", error);
  } finally {
    setApiLoading(false);
    // Always reset the user context loading state
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: false,
    }));
  }
};

export default function Game({ paused = false }) {
  const { updateUserInfo, updatewatchScreenInfo } = useUserInfo();
  const [fruits, setFruits] = useState([]);
  const [score, setScore] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const [highScore, setHighScore] = useState(() => {
    return Number(localStorage.getItem("highScore")) || 0;
  });

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsPageVisible(isVisible);

      // Clear fruits when page becomes hidden to prevent pileup
      if (!isVisible) {
        setFruits([]);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  const [apiHighScore, setApiHighScore] = useState(0);
  const [trailPoints, setTrailPoints] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [pausedState, setPausedState] = useState(false);
  const [showStart, setShowStart] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);

  const stageRef = useRef();
  const clearTrailTimeout = useRef(null);
  const bombSoundPlaying = useRef(false);

  useEffect(() => {
    setPausedState(paused);
  }, [paused]);

  useEffect(() => {
    const fetchInitialHighScore = async () => {
      try {
        const res = await getUserDetails1();
        if (res && res.gameRewards && res.gameRewards.games) {
          const fruitsliceGame = res.gameRewards.games.find(
            (game) => game.gameType === "fruitslice"
          );
          if (fruitsliceGame && fruitsliceGame.highScore) {
            setApiHighScore(fruitsliceGame.highScore);
            // Update local high score if API high score is higher
            if (fruitsliceGame.highScore > highScore) {
              setHighScore(fruitsliceGame.highScore);
              localStorage.setItem(
                "fruitsliceHighScore",
                fruitsliceGame.highScore.toString()
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch initial high score:", error);
      }
    };

    fetchInitialHighScore();
  }, []);

  useEffect(() => {
    if (pausedState || gameOver || showStart || !isPageVisible) return;

    const interval = setInterval(() => {
      setFruits((f) => [
        ...f,
        randomFruit(window.innerWidth, window.innerHeight),
        randomFruit(window.innerWidth, window.innerHeight),
      ]);
    }, 700);
    return () => clearInterval(interval);
  }, [pausedState, gameOver, showStart, isPageVisible]);

  useEffect(() => {
    if (pausedState || gameOver || showStart || !isPageVisible) return;
    let animFrame;

    const update = () => {
      setFruits((fruits) =>
        fruits
          .map((f) => ({
            ...f,
            x: f.x + f.vx,
            y: f.y + f.vy,
            vy: f.vy + 0.2,
          }))
          .filter((f) => f.y < window.innerHeight + 50)
      );
      animFrame = requestAnimationFrame(update);
    };
    animFrame = requestAnimationFrame(update);

    return () => cancelAnimationFrame(animFrame);
  }, [pausedState, gameOver, showStart, isPageVisible]);

  const handlePointerMove = (e) => {
    if (pausedState || gameOver || showStart) return;
    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    if (pos) {
      setTrailPoints((points) => [...points.slice(-20), pos.x, pos.y]);
      checkSlice(pos);

      if (clearTrailTimeout.current) {
        clearTimeout(clearTrailTimeout.current);
      }
      clearTrailTimeout.current = setTimeout(() => {
        setTrailPoints([]);
      }, 300);
    }
  };

  const checkSlice = (pos) => {
    let slicedFruit = false;
    setFruits((fruits) =>
      fruits.map((f) => {
        if (
          !f.sliced &&
          Math.hypot(f.x - pos.x, f.y - pos.y) < FRUIT_SIZE / 2
        ) {
          slicedFruit = true;
          if (f.type === "bomb") {
            if (!bombSoundPlaying.current) {
              bombSoundPlaying.current = true;
              bombSound.play();
              bombSound.once("end", () => {
                bombSoundPlaying.current = false;
              });
            }
            setGameOver(true);
            setFruits([]);
            return f;
          } else {
            // sliceSound.play(); // uncomment if you want slice sound
            setScore((s) => s + 10);
            return { ...f, sliced: true };
          }
        }
        return f;
      })
    );
    return slicedFruit;
  };

  const removeSliced = (id) => {
    setFruits((f) => f.filter((fruit) => fruit.id !== id));
  };

  useEffect(() => {
    if (gameOver) {
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem("highScore", score);
      }
      // Submit score to API
      handleApiResponse(
        score,
        updateUserInfo,
        updatewatchScreenInfo,
        setApiLoading
      ).then((apiHighScore) => {
        if (apiHighScore && apiHighScore > highScore) {
          setApiHighScore(apiHighScore);
          setHighScore(apiHighScore);
          localStorage.setItem("fruitsliceHighScore", apiHighScore.toString());
        }
      });
    }
  }, [gameOver, score, highScore]);

  const restartGame = () => {
    setScore(0);
    setGameOver(false);
    setShowStart(false);
    setApiLoading(false); // Reset loading state when restarting
    // Also reset user context loading state
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  const togglePause = () => {
    setPausedState((p) => !p);
  };

  const startGame = () => {
    setShowStart(false);
    setGameOver(false);
    setScore(0);
    setApiLoading(false); // Reset loading state when starting
    // Also reset user context loading state
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Background gradient */}
      <div
        style={{
          position: "fixed",
          width: "100vw",
          height: "100vh",
          background: "linear-gradient(135deg, #453522, #2a2a2a)",
          zIndex: -1,
        }}
      />

      {/* Start Screen */}
      {showStart && (
        <div
          className="overlay-fruitslice fade-in"
          style={{
            background: "rgba(0,0,0,0.9)",
            padding: "2rem 3rem",
            borderRadius: "16px",
            // border: "2px solid #29ad23",
            boxShadow: "0 0 20px rgba(41, 173, 35, 0.3)",
            maxWidth: "320px",
            textAlign: "center",
            color: "#4df038",
            userSelect: "none",
            fontFamily: "myFirstFont, monospace",
          }}
        >
          <h1
            style={{
              marginBottom: "0.5rem",
              fontWeight: "900",
              fontSize: "2rem",
              textTransform: "uppercase",
              letterSpacing: "2px",
              textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
            }}
          >
            Fruit Slicer
          </h1>
          <p
            style={{
              marginBottom: "1.5rem",
              fontSize: "1rem",
              padding: "15px",
              color: "#ffffff",
              lineHeight: "1.4",
            }}
          >
            Slice the fruits and avoid bombs! Use mouse or touch.
          </p>
          <button
            className="button"
            onClick={startGame}
            style={{
              backgroundColor: "#000000",
              color: "#4df038",
              fontWeight: "700",
              fontSize: "1.1rem",
              padding: "0.75rem 2rem",
              borderRadius: "8px",
              border: "2px solid #29ad23",
              cursor: "pointer",
              transition: "all 0.3s ease",
              textTransform: "uppercase",
              letterSpacing: "1px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#002F03";
              e.currentTarget.style.borderColor = "#4df038";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#000000";
              e.currentTarget.style.borderColor = "#29ad23";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            Start Game
          </button>
        </div>
      )}

      {/* Pause Overlay */}
      {pausedState && !gameOver && !showStart && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.9)",
            color: "#4df038",
            fontSize: "2rem",
            fontWeight: "700",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
            userSelect: "none",
            textTransform: "uppercase",
            fontFamily: "myFirstFont, monospace",
            letterSpacing: "2px",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
            // border: "2px solid #29ad23"
          }}
        >
          Paused
        </div>
      )}

      {/* Game Over Overlay */}
      {gameOver && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.9)",
            color: "#4df038",
            fontSize: "2rem",
            fontWeight: "700",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
            userSelect: "none",
            textTransform: "uppercase",
            fontFamily: "myFirstFont, monospace",
            letterSpacing: "2px",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
            // border: "2px solid #29ad23"
          }}
        >
          {apiLoading ? (
            <>
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "5px solid #f3f3f3",
                  borderTop: "5px solid #4df038",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginBottom: "20px",
                }}
              ></div>
              <div style={{ fontSize: "1.5rem", color: "#87ceeb" }}>
                Processing...
              </div>
            </>
          ) : (
            <>
              Game Over
              <div
                style={{
                  fontSize: "1.5rem",
                  marginTop: "1rem",
                  textTransform: "uppercase",
                  color: "#87ceeb",
                }}
              >
                Score: {score}
              </div>
              <div
                style={{
                  fontSize: "1.2rem",
                  marginTop: "0.5rem",
                  textTransform: "uppercase",
                  color:
                    score > Math.max(highScore, apiHighScore)
                      ? "#ffd700"
                      : "#ffa500",
                }}
              >
                {score > Math.max(highScore, apiHighScore)
                  ? "🎉 NEW HIGH SCORE! 🎉"
                  : `High Score: ${Math.max(highScore, apiHighScore)}`}
              </div>
            </>
          )}
          {!apiLoading && (
            <button
              onClick={restartGame}
              style={{
                marginTop: "2rem",
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
                borderRadius: "8px",
                backgroundColor: "#000000",
                color: "#4df038",
                border: "2px solid #29ad23",
                cursor: "pointer",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "1px",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#002F03";
                e.currentTarget.style.borderColor = "#4df038";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#000000";
                e.currentTarget.style.borderColor = "#29ad23";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              Restart
            </button>
          )}
        </div>
      )}

      {/* Pause Button */}
      {!gameOver && !showStart && (
        <button
          style={{
            position: "fixed",
            bottom: "2%",
            right: "2%",
            padding: "0.5rem 1rem",
            fontSize: "1.3rem",
            borderRadius: "8px",
            backgroundColor: "#000000",
            color: "#4df038",
            border: "2px solid #29ad23",
            cursor: "pointer",
            zIndex: 10,
            fontWeight: "700",
            width: "50px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
          }}
          onClick={togglePause}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#002F03";
            e.currentTarget.style.borderColor = "#4df038";
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#000000";
            e.currentTarget.style.borderColor = "#29ad23";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          {pausedState ? "▶" : "⏸"}
        </button>
      )}

      {/* Score Display */}
      <div
        style={{
          position: "fixed",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          padding: "15px",
          color: "#4df038",
          width: "100%",
          textAlign: "center",
          fontWeight: "700",
          fontSize: "1.25rem",
          textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
          userSelect: "none",
          textTransform: "uppercase",
          zIndex: 1,
          fontFamily: "myFirstFont, monospace",
          letterSpacing: "1px",
          borderBottom: "2px solid #29ad23",
        }}
      >
        Score: {score}
        {/* | Highscore: {Math.max(highScore, apiHighScore)} */}
      </div>

      {/* Konva Stage */}
      <Stage
        width={window.innerWidth}
        height={window.innerHeight}
        onMouseMove={handlePointerMove}
        onTouchMove={handlePointerMove}
        ref={stageRef}
        style={{ touchAction: "none" }}
      >
        <Layer>
          {fruits.map((fruit) => (
            <FruitImage
              key={fruit.id}
              id={fruit.id}
              x={fruit.x}
              y={fruit.y}
              image={fruit.image}
              sliced={fruit.sliced}
              removeSliced={removeSliced}
            />
          ))}

          {/* Trail */}
          <Line
            points={trailPoints}
            stroke="#f90"
            strokeWidth={8}
            tension={0.5}
            lineCap="round"
            lineJoin="round"
            globalCompositeOperation="lighter"
            shadowColor="#ff8000"
            shadowBlur={8}
          />
        </Layer>
      </Stage>
    </>
  );
}
